export type MiniMenuPublicLoadState = 'loading' | 'ready' | 'unavailable' | 'error';
export type MiniMenuPublicLanguage = 'en' | 'zh-CN';

export interface MiniMenuPublicLanguageOption {
  code: MiniMenuPublicLanguage;
  label: string;
  shortLabel: string;
}

// Evolution renders every uploaded image into three device crops. The MiniMenu layout
// uses all three: the landing band and the What's On detail pane are portrait in BOTH
// tablet orientations, so a landscape tablet and a portrait tablet need different crops.
export interface MiniMenuPublicImageSet {
  mobile: string | null;
  tablet: string | null;
  tabletPortrait: string | null;
}

export interface MiniMenuPublicTheme {
  font: string;
  backgroundColor: string;
  uiColor: string;
  contentColor: string;
  accentColor: string;
  landingLogoUrl: string | null;
  bannerLogoUrl: string | null;
  landingBackground: MiniMenuPublicImageSet | null;
}

export interface MiniMenuPublicModifierOption {
  id: string;
  name: string;
  priceDelta: number;
}

/**
 * A named modifier group — Evolution's `groupModifierList` entry. The title is the
 * thing the guest reads ("Size", "Sugar level"); the options are its variants.
 */
export interface MiniMenuPublicModifierGroup {
  id: string;
  name: string;
  options: MiniMenuPublicModifierOption[];
}

/**
 * A modifier SET is a group of groups. In Evolution it exists purely so an operator
 * can attach several groups to a product in one action instead of re-adding each
 * group in sequence. It carries no meaning for the guest, so the public menu renders
 * a set's groups exactly as if they had been attached individually — the set name is
 * kept only for diagnostics and admin-side traceability.
 */
export interface MiniMenuPublicModifierSet {
  id: string;
  name: string;
  groups: MiniMenuPublicModifierGroup[];
}

/**
 * The three Evolution modifier shapes, kept distinct instead of flattened:
 *
 * - `single` — loose modifiers attached straight to the product with no group. They
 *   render bare, with no heading, because there is no group title to show.
 * - `groups` — named groups; render the title, then its variants.
 * - `sets`   — groups of groups; render each contained group like a normal group.
 */
export interface MiniMenuPublicModifiers {
  single: MiniMenuPublicModifierOption[];
  groups: MiniMenuPublicModifierGroup[];
  sets: MiniMenuPublicModifierSet[];
}

export interface MiniMenuPublicProduct {
  id: string;
  categoryId: string;
  name: string;
  description: string;
  price: number;
  images: string[];
  modifiers: MiniMenuPublicModifiers;
}

export interface MiniMenuPublicCategory {
  id: string;
  name: string;
  products: MiniMenuPublicProduct[];
}

export interface MiniMenuPublicPromotion {
  id: string;
  badge: string;
  title: string;
  when: string;
  lead: string;
  body: string[];
  image: MiniMenuPublicImageSet | null;
  sortOrder: number;
}

export interface MiniMenuPublicTranslation {
  teaser?: {
    location?: string;
    eyebrow?: string;
    subline?: string;
  };
  categories?: Record<string, {
    name?: string;
    products?: Record<string, {
      name?: string;
      description?: string;
    }>;
  }>;
  promotions?: Record<string, {
    badge?: string;
    title?: string;
    when?: string;
    lead?: string;
    body?: string[];
  }>;
}

export interface MiniMenuPublicContent {
  loadState: MiniMenuPublicLoadState;
  slug: string;
  businessName: string;
  businessPageUrl: string | null;
  location: string;
  eyebrow: string;
  subline: string;
  currency: string;
  // Evolution reports whether the site may take orders. It is additionally gated by
  // MINI_MENU_ORDERING_ENABLED in the service while ordering is switched off.
  orderingAvailable: boolean;
  theme: MiniMenuPublicTheme;
  languages: MiniMenuPublicLanguageOption[];
  defaultLanguage: MiniMenuPublicLanguage;
  translations: Record<string, MiniMenuPublicTranslation>;
  categories: MiniMenuPublicCategory[];
  promotions: MiniMenuPublicPromotion[];
  usesStagingFallback: boolean;
}

export function emptyMiniMenuModifiers(): MiniMenuPublicModifiers {
  return { single: [], groups: [], sets: [] };
}

export function hasMiniMenuModifiers(modifiers: MiniMenuPublicModifiers | undefined): boolean {
  if (!modifiers) {
    return false;
  }
  return Boolean(
    modifiers.single.length
    || modifiers.groups.some((group) => group.options.length)
    || modifiers.sets.some((set) => set.groups.some((group) => group.options.length))
  );
}

export const LOADING_MINI_MENU_PUBLIC_CONTENT: MiniMenuPublicContent = {
  loadState: 'loading',
  slug: '',
  businessName: 'HUNGERY',
  businessPageUrl: null,
  location: 'HUNGERY',
  eyebrow: 'Digital menu',
  subline: 'Loading the menu...',
  currency: 'Rp',
  orderingAvailable: false,
  theme: {
    font: 'hanken',
    backgroundColor: '#ECECEA',
    uiColor: '#001E46',
    contentColor: '#16242B',
    accentColor: '#887A57',
    landingLogoUrl: null,
    bannerLogoUrl: null,
    landingBackground: null
  },
  languages: [{ code: 'en', label: 'English', shortLabel: 'EN' }],
  defaultLanguage: 'en',
  translations: {},
  categories: [],
  promotions: [],
  usesStagingFallback: false
};
